// Background upload queue for photos captured offline-ish.
//
// Flow:
//   1. Capture writes the blob to pendingPhotoStore (IDB) and returns
//      immediately — no upload yet. UI shows the local thumb.
//   2. enqueue() kicks the drain loop. drain() processes pending entries
//      one at a time through runPipeline (compress + storage upload +
//      photos INSERT). On success: remove from IDB, invalidate
//      react-query so the server row replaces the local one in the grid.
//      On failure: exponential backoff with bounded retries, then mark
//      as 'failed' for a manual retry surface (added later).
//   3. drain() is also re-kicked on init (App boot) so leftovers from
//      previous sessions resume automatically.
//
// Concurrency: serial for v1 — keep it simple, mobile bandwidth is the
// bottleneck anyway. The per-photo pipeline already parallelizes its
// 3-4 storage uploads via Promise.all inside runPipeline.

import type { QueryClient } from '@tanstack/react-query'
import { supabase, STORAGE_BUCKET } from './supabaseClient'
import { resizeImage, resizeImageToSizes } from './utils'
import { renderMarkupToImage } from './markupUtils'
import { logger, describeError } from '../utils/logger'
import * as store from './pendingPhotoStore'
import * as mutationStore from './pendingMutationStore'
import type { PendingPhoto } from './pendingPhotoStore'

const MAX_ATTEMPTS = 5
const BASE_BACKOFF_MS = 1500       // doubles each attempt; capped at 30s
const PER_UPLOAD_TIMEOUT_MS = 45_000  // hard cap on a single storage upload — prevents hangs when reception drops mid-fetch

let queryClient: QueryClient | null = null
let isDraining = false
let kickPending = false        // re-kick request that arrived during a drain

export function configureQueue(opts: { queryClient: QueryClient }) {
  queryClient = opts.queryClient
}

export async function enqueue(photo: PendingPhoto): Promise<void> {
  await store.put(photo)
  kick()
}

// Public API used by App.tsx after auth is ready.
export async function initQueue(): Promise<void> {
  // Any 'uploading' entry was interrupted by a page reload — reset to
  // 'pending' so drain picks it up again.
  const recovered = await store.recoverInterrupted()
  if (recovered > 0) {
    logger.info('upload-queue', `recovered ${recovered} interrupted uploads`)
  }

  // Wire the 'online' event so a real offline→online transition revives
  // and retries the queue. Idempotent — multiple initQueue calls don't
  // stack listeners. We intentionally do NOT gate drain() on
  // navigator.onLine (see drain()): iOS Safari PWAs routinely report
  // onLine===false while fetch still works, which used to wedge the
  // queue with no recovery path.
  if (!networkListenersWired && typeof window !== 'undefined') {
    networkListenersWired = true
    window.addEventListener('online', () => { void resumeQueue('online-event') })
  }

  // Periodic safety-net re-kick: iOS may freeze/thaw our process or miss
  // the 'online' event entirely, leaving pending photos idle. A cheap
  // interval re-kicks the drain so a stuck backlog always makes progress
  // once the app is running. No-op when the queue is empty/already draining.
  if (periodicTimer == null && typeof window !== 'undefined') {
    periodicTimer = window.setInterval(() => { kick() }, PERIODIC_KICK_MS) as unknown as number
  }

  kick()
}

let networkListenersWired = false
let periodicTimer: number | null = null
const PERIODIC_KICK_MS = 60_000

// Cap on how many times resumeQueue will auto-revive a 'failed' photo
// before leaving it settled as 'failed' for manual retry. Without this, a
// genuinely-undecodable photo ("Could not load image" on truly-bad bytes)
// would revive → burn 5 attempts → fail → revive … forever on every
// foreground/online event, spinning the overlay and re-doing work.
const MAX_REVIVALS = 2

// Revive interrupted/failed entries and re-kick the drain. Called on the
// 'online' event and whenever the app returns to the foreground
// (visibilitychange/focus/pageshow, wired in App.tsx). This is the
// recovery path that replaces the old navigator.onLine drain guard:
// bringing the app forward — or regaining connectivity — retries anything
// recoverable. A 'failed' photo that has exhausted its revival budget is
// left alone (surfaced as a "Tap to retry" tile via retryPhoto).
export async function resumeQueue(reason: string): Promise<void> {
  const all = await store.getAll()
  let revived = 0
  for (const p of all) {
    if (p.status === 'uploading') {
      // Interrupted mid-flight (reload/freeze) — not a real failure; always resume.
      await store.update(p.local_id, { status: 'pending', attempts: 0, last_error: null })
      revived++
    } else if (p.status === 'failed' && (p.revivals ?? 0) < MAX_REVIVALS) {
      // Hit MAX_ATTEMPTS but still within the revival budget — give it another pass.
      await store.update(p.local_id, {
        status: 'pending', attempts: 0, last_error: null, revivals: (p.revivals ?? 0) + 1,
      })
      revived++
    }
    // else: failed + budget exhausted → leave for manual retryPhoto().
  }
  if (revived > 0) logger.info('upload-queue', `resume (${reason}) — revived ${revived} entries`)
  kick()
}

// Manual retry from the grid's failed tile. Full reset (clears both the
// attempt and revival budgets) because the user explicitly asked.
export async function retryPhoto(localId: string): Promise<void> {
  await store.update(localId, { status: 'pending', attempts: 0, revivals: 0, last_error: null })
  logger.info('upload-queue', 'manual retry', { local_id: localId })
  kick()
}

function kick() {
  if (isDraining) {
    kickPending = true
    return
  }
  void drain()
}

async function drain() {
  if (isDraining) {
    kickPending = true
    return
  }
  // NOTE: we deliberately do NOT gate on navigator.onLine here. On iOS
  // Safari PWAs that flag is unreliable — it gets stuck reporting false
  // while regular fetch still works, which previously wedged the queue
  // permanently (the boot kick bailed and no 'online' event ever fired).
  // Genuine offline is already handled: each upload has a 45s timeout and
  // failures back off + retry, and resumeQueue() revives them on
  // reconnect/foreground. So we just attempt, and let the pipeline decide.
  isDraining = true
  try {
    while (true) {
      const next = await store.getNextPending()
      if (!next) break
      await processOne(next)
    }
  } finally {
    isDraining = false
    if (kickPending) {
      kickPending = false
      void drain()
    }
  }
}

async function processOne(p: PendingPhoto) {
  await store.update(p.local_id, { status: 'uploading' })

  try {
    await runPipeline(p)
    // Pipeline already cleared the mutation row on success; remove the
    // photo entry now and bust react-query caches so the server row
    // replaces the local placeholder in the grid (now with any markup
    // baked in, tags attached, and issue status set).
    await store.remove(p.local_id)
    queryClient?.invalidateQueries({ queryKey: ['photos', p.project_id] })
    queryClient?.invalidateQueries({ queryKey: ['photos-count', p.project_id] })
    queryClient?.invalidateQueries({ queryKey: ['feed'] })
    queryClient?.invalidateQueries({ queryKey: ['projects'] })  // photoCount changed
    queryClient?.invalidateQueries({ queryKey: ['markup', p.local_id] })
    queryClient?.invalidateQueries({ queryKey: ['photo-tags', p.local_id] })
  } catch (err) {
    const attempts = p.attempts + 1
    const message = describeError(err)
    logger.warn('upload-queue', 'photo upload failed', { local_id: p.local_id, attempts, error: message })
    if (attempts >= MAX_ATTEMPTS) {
      await store.update(p.local_id, { status: 'failed', attempts, last_error: message })
    } else {
      // Re-queue with backoff. We set status back to 'pending' AFTER the
      // delay so drain doesn't loop hot on the same failing photo.
      const delay = Math.min(30_000, BASE_BACKOFF_MS * 2 ** (attempts - 1))
      setTimeout(() => {
        void store.update(p.local_id, { status: 'pending', attempts, last_error: message }).then(() => kick())
      }, delay)
      // Leave it as 'uploading' so drain() skips it; the setTimeout above
      // will flip it back to 'pending' after backoff. Without this guard
      // drain would tight-loop the same failed photo.
    }
  }
}

// Bounded wait for a single storage upload. Resolves to the upload
// result if it lands before the timeout; rejects with a recognizable
// error otherwise. The catch path treats this like any other failure
// and schedules backoff/retry.
function withTimeout<T>(p: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => {
      reject(new Error(`upload timeout (${label}) after ${PER_UPLOAD_TIMEOUT_MS}ms`))
    }, PER_UPLOAD_TIMEOUT_MS)
    p.then(
      v => { clearTimeout(id); resolve(v) },
      err => { clearTimeout(id); reject(err) },
    )
  })
}

// ── pipeline: blob → 3-4 storage uploads → photos INSERT ─────────────────────
// Mirrors the logic that used to live inline in usePhotos.uploadPhotoMutation
// but parallelizes the storage uploads, since they're independent.

async function runPipeline(p: PendingPhoto): Promise<void> {
  const t = logger.time('photo-upload', 'queue pipeline', {
    local_id: p.local_id,
    fileSize: p.original_blob.size,
    projectId: p.project_id,
    isHd: p.is_hd,
  })

  try {
    // Extract EXIF before resize (resize strips it).
    let exifTakenAt: string | null = null
    let exifLatitude: number | null = null
    let exifLongitude: number | null = null
    try {
      const { default: exifr } = await import('exifr')
      const exif = await exifr.parse(p.original_blob, { tiff: true, exif: true, gps: true })
      if (exif?.DateTimeOriginal) {
        exifTakenAt = new Date(exif.DateTimeOriginal).toISOString()
      }
      if (exif?.latitude != null && exif?.longitude != null) {
        exifLatitude = exif.latitude
        exifLongitude = exif.longitude
      }
    } catch (exifErr) {
      logger.warn('photo-upload', 'EXIF parse failed — using defaults', { error: String(exifErr) })
    }

    const photoId = p.local_id  // local_id IS the server id
    const timestamp = Date.now()
    const basePath = `${p.project_id}/${photoId}`

    // Read any mutations the user queued while the photo was pending
    // (markup / tags / issue status). Read once here so we have a
    // stable snapshot through the rest of the pipeline.
    const mutations = await mutationStore.get(p.local_id)
    const hasMarkup = !!(mutations?.markup_data?.elements?.length)

    // Compress all three sizes from a SINGLE decode of the original.
    // Decoding the full-res photo once and drawing three sizes keeps peak
    // memory low; the old 3×-parallel `new Image()` decode is what blew
    // iOS Safari's memory ceiling and failed with "Could not load image"
    // on a queued backlog. (See resizeImageToSizes.)
    const [webBlob, thumbBlobRaw, smallThumbBlob] = await resizeImageToSizes(p.original_blob, [
      { maxWidth: 1200, quality: 0.85 },
      { maxWidth: 400, quality: 0.8 },
      { maxWidth: 150, quality: 0.8 },
    ])

    // If the user marked up the photo while it was pending, bake the
    // markup into a full-res image locally (we still have the original
    // blob) and regenerate the thumbnail from that baked image so the
    // marked-up image is what shows up in the grid right away.
    let markedUpBlob: Blob | null = null
    let thumbBlob = thumbBlobRaw
    if (hasMarkup && mutations?.markup_data) {
      const blobUrl = URL.createObjectURL(p.original_blob)
      try {
        markedUpBlob = await renderMarkupToImage(blobUrl, mutations.markup_data)
        thumbBlob = await resizeImage(markedUpBlob, 400, 0.8)
      } finally {
        URL.revokeObjectURL(blobUrl)
      }
    }

    const webPath         = `${basePath}/web_${timestamp}.jpg`
    const thumbPath       = `${basePath}/thumb_${timestamp}.jpg`
    const smallThumbPath  = `${basePath}/small_thumb_${timestamp}.jpg`
    const originalPath    = p.is_hd ? `${basePath}/original_${timestamp}.jpg` : webPath
    const markedUpPath    = `${basePath}/marked_up_${timestamp}.jpg`

    // Upload all variants in parallel. Each one wrapped in a timeout so
    // a hung fetch (network dropped mid-request) can't block the queue
    // forever — supabase-js storage doesn't currently accept an
    // AbortSignal, so Promise.race is the lever we have.
    const uploads: Promise<{ error: Error | null }>[] = [
      withTimeout(supabase.storage.from(STORAGE_BUCKET).upload(webPath, webBlob, { contentType: 'image/jpeg' }), 'web'),
      withTimeout(supabase.storage.from(STORAGE_BUCKET).upload(thumbPath, thumbBlob, { contentType: 'image/jpeg' }), 'thumb'),
      withTimeout(supabase.storage.from(STORAGE_BUCKET).upload(smallThumbPath, smallThumbBlob, { contentType: 'image/jpeg' }), 'smallThumb'),
    ]
    if (p.is_hd) {
      uploads.push(
        withTimeout(supabase.storage.from(STORAGE_BUCKET).upload(originalPath, p.original_blob, { contentType: 'image/jpeg' }), 'original'),
      )
    }
    if (markedUpBlob) {
      uploads.push(
        withTimeout(supabase.storage.from(STORAGE_BUCKET).upload(markedUpPath, markedUpBlob, { contentType: 'image/jpeg' }), 'markedUp'),
      )
    }
    const results = await Promise.all(uploads)
    for (const r of results) {
      if (r.error) throw r.error
    }

    // Upsert the photo row (ON CONFLICT (id) DO NOTHING). Folds in any
    // queued issue_status + marked_up_path so the server row is consistent
    // with what the user saw locally.
    //
    // Why upsert-ignore instead of insert: a prior attempt can succeed at
    // the DB insert but lose the response (network timeout, or the PWA is
    // killed) before we remove the local queue entry. The retry then hits
    // a duplicate-key error on photos_pkey forever — a poison pill that
    // spins the "uploading" overlay indefinitely even though the photo IS
    // on the server. ignoreDuplicates turns that into a no-op success, so
    // the retry completes and the local entry is finally cleared.
    const { error: dbError } = await supabase
      .from('photos')
      .upsert({
        id: photoId,
        project_id: p.project_id,
        user_id: p.user_id,
        photographer_name: p.photographer_name,
        original_path: originalPath,
        web_path: webPath,
        thumbnail_path: thumbPath,
        small_thumbnail_path: smallThumbPath,
        marked_up_path: markedUpBlob ? markedUpPath : null,
        latitude: exifLatitude ?? p.latitude,
        longitude: exifLongitude ?? p.longitude,
        taken_at: exifTakenAt ?? p.captured_at,
        uploaded_at: exifTakenAt ?? p.captured_at,
        issue_status: mutations?.issue_status ?? 'not_an_issue',
      }, { onConflict: 'id', ignoreDuplicates: true })
    if (dbError) throw dbError

    // Flush remaining queued mutations: markup row + tag rows. Failures
    // here are logged but don't fail the whole upload — the photo is
    // already on the server, just without its decorations. The grid
    // refresh will reflect server state regardless.
    if (mutations?.markup_data) {
      const { error: markupErr } = await supabase
        .from('markups')
        .insert({ photo_id: photoId, markup_data: mutations.markup_data })
      if (markupErr) {
        logger.warn('photo-upload', 'failed to insert queued markup', { photoId, error: markupErr.message })
      }
    }
    if (mutations?.tag_ids?.length) {
      const rows = mutations.tag_ids.map(tag_id => ({ photo_id: photoId, tag_id }))
      const { error: tagsErr } = await supabase
        .from('photo_tags')
        .upsert(rows, { onConflict: 'photo_id,tag_id', ignoreDuplicates: true })
      if (tagsErr) {
        logger.warn('photo-upload', 'failed to insert queued tags', { photoId, count: rows.length, error: tagsErr.message })
      }
    }

    // Drop the mutation row on success — it's been applied server-side.
    if (mutations) {
      await mutationStore.clear(p.local_id)
    }

    t.done({ photoId, markup: hasMarkup, tags: mutations?.tag_ids?.length ?? 0, issue_status: mutations?.issue_status ?? 'not_an_issue' })
  } catch (err) {
    t.fail(err)
    throw err
  }
}
